import { Link } from "react-router-dom";
import { Globe2, Zap, Layers, Handshake, CreditCard, Headset, ArrowRight, CheckCircle2 } from "lucide-react";
import usePageMeta from "../../hooks/usePageMeta";
import { useCopy } from "../../context/CopyContext";

const IMG_HERO = "https://images.unsplash.com/photo-1504150558240-0b4fd8946624?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const IMG_NETWORK = "https://images.unsplash.com/photo-1758073519996-6d3c63b4922c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const IMG_HANDSHAKE = "https://images.pexels.com/photos/8112186/pexels-photo-8112186.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const VP_ICONS = [Globe2, Layers, Handshake, Zap, CreditCard, Headset];
const POS_LINKS = ["/what-is-sellvago", "/sellvago-vs-b2b-ota", "/how-white-label-works", "/how-resellers-grow"];

export default function Home() {
  const t = useCopy();
  usePageMeta(
    "Sellvago — Content + technology platform for independent travel resellers",
    "Sellvago is a travel content and selling platform that enables travel resellers to independently service travellers under their own brand name."
  );

  const stats = [1, 2, 3, 4].map((i) => ({ v: t(`b2b.stat${i}.v`), l: t(`b2b.stat${i}.l`) }));
  const positioning = [1, 2, 3, 4].map((i) => ({
    title: t(`b2b.pos${i}.title`), text: t(`b2b.pos${i}.text`), cta: t(`b2b.pos${i}.cta`), to: POS_LINKS[i - 1],
  }));
  const valueProps = [1, 2, 3, 4, 5, 6].map((i) => ({
    icon: VP_ICONS[i - 1], title: t(`b2b.vp${i}.title`), text: t(`b2b.vp${i}.text`),
  }));
  const testimonials = [1, 2, 3].map((i) => ({
    quote: t(`b2b.tes${i}.quote`), name: t(`b2b.tes${i}.name`), role: t(`b2b.tes${i}.role`),
  }));

  return (
    <div>
      <section className="relative pt-32 pb-24 px-6 lg:px-10 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMG_HERO} alt="Business travel professional at airport" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#00172D] via-[#00172D]/90 to-[#00172D]/40" />
        </div>
        <div className="relative max-w-7xl mx-auto">
          <div className="max-w-2xl fade-up">
            <p className="text-xs uppercase tracking-[0.2em] text-[#E8767A] font-bold mb-5">{t("b2b.hero.overline")}</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white leading-[1.05]">
              {t("b2b.hero.title")}
            </h1>
            <p className="mt-6 text-base md:text-lg text-slate-400 leading-relaxed max-w-xl" data-speakable>
              {t("b2b.hero.subtitle")}
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/contact" data-testid="hero-demo-cta" className="bg-[#970C10] hover:bg-[#7A0A0D] text-white font-semibold px-7 py-3.5 rounded-sm transition-colors duration-200 flex items-center gap-2">
                {t("b2b.hero.cta1")} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/what-is-sellvago" data-testid="hero-what-is-cta" className="glass-dark text-white font-semibold px-7 py-3.5 rounded-sm hover:bg-white/10 transition-colors duration-200">
                {t("b2b.hero.cta2")}
              </Link>
            </div>
          </div>
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map((s) => (
              <div key={s.l} className="glass-dark rounded-sm p-6" data-testid={`stat-${s.l.replace(/[^a-z0-9]+/g, "-")}`}>
                <p className="font-heading text-3xl font-bold text-white">{s.v}</p>
                <p className="text-xs uppercase tracking-[0.15em] text-slate-500 mt-2">{s.l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 lg:px-10 py-16" data-testid="home-positioning-band">
        <div className="max-w-7xl mx-auto grid md:grid-cols-4 gap-5">
          {positioning.map((c) => (
            <Link
              key={c.to}
              to={c.to}
              data-testid={`home-positioning--${c.to.replace(/^\//, "").replace(/\//g, "-")}`}
              className="glass-dark rounded-sm p-7 hover:bg-white/[0.07] transition-colors duration-200 group"
            >
              <h3 className="font-heading font-bold text-white text-lg" data-speakable>{c.title}</h3>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed" data-speakable>{c.text}</p>
              <p className="mt-4 text-xs uppercase tracking-[0.15em] text-[#E8767A] font-bold flex items-center gap-1">
                {c.cta} <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform duration-200" />
              </p>
            </Link>
          ))}
        </div>
      </section>

      <section className="px-6 lg:px-10 py-24">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs uppercase tracking-[0.2em] text-[#E8767A] font-bold mb-3">{t("b2b.why.overline")}</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white max-w-xl">
            {t("b2b.why.title")}
          </h2>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {valueProps.map((v) => (
              <div key={v.title} className="glass-dark rounded-sm p-8 hover:bg-white/[0.07] transition-colors duration-200" data-testid={`value-prop-${v.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <v.icon className="w-6 h-6 text-[#E8767A] mb-5" />
                <h3 className="font-heading font-bold text-white text-lg">{v.title}</h3>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 lg:px-10 py-24">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#E8767A] font-bold mb-3">{t("b2b.sup.overline")}</p>
            <h2 className="font-heading text-3xl font-bold tracking-tight text-white">
              {t("b2b.sup.title")}
            </h2>
            <p className="mt-5 text-slate-400 leading-relaxed">
              {t("b2b.sup.text")}
            </p>
            <ul className="mt-8 space-y-4">
              {[t("b2b.sup.li1"), t("b2b.sup.li2"), t("b2b.sup.li3"), t("b2b.sup.li4")].map((li) => (
                <li key={li} className="flex items-start gap-3 text-sm text-slate-300">
                  <CheckCircle2 className="w-5 h-5 text-[#970C10] shrink-0 mt-0.5" /> {li}
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <img src={IMG_NETWORK} alt="Global airline network data visualization" className="rounded-sm border border-white/10 w-full object-cover h-80 lg:h-[420px]" />
            <div className="absolute -bottom-6 -left-6 glass-dark rounded-sm p-5 hidden md:block">
              <p className="font-heading text-2xl font-bold text-white">{t("b2b.sup.statv")}</p>
              <p className="text-xs text-slate-500 uppercase tracking-[0.15em] mt-1">{t("b2b.sup.statl")}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 lg:px-10 py-24">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div className="order-2 lg:order-1 relative">
            <img src={IMG_HANDSHAKE} alt="Business partnership" className="rounded-sm border border-white/10 w-full object-cover h-80 lg:h-[420px]" />
          </div>
          <div className="order-1 lg:order-2">
            <p className="text-xs uppercase tracking-[0.2em] text-[#E8767A] font-bold mb-3">{t("b2b.par.overline")}</p>
            <h2 className="font-heading text-3xl font-bold tracking-tight text-white">
              {t("b2b.par.title")}
            </h2>
            <p className="mt-5 text-slate-400 leading-relaxed">
              {t("b2b.par.text")}
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/white-label-portal" data-testid="home-whitelabel-link" className="glass-dark text-white text-sm font-semibold px-6 py-3 rounded-sm hover:bg-white/10 transition-colors duration-200">
                {t("b2b.par.btn1")}
              </Link>
              <Link to="/affiliate-program" data-testid="home-affiliate-link" className="glass-dark text-white text-sm font-semibold px-6 py-3 rounded-sm hover:bg-white/10 transition-colors duration-200">
                {t("b2b.par.btn2")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 lg:px-10 py-24">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs uppercase tracking-[0.2em] text-[#E8767A] font-bold mb-3">{t("b2b.tes.overline")}</p>
          <h2 className="font-heading text-3xl font-bold tracking-tight text-white">{t("b2b.tes.title")}</h2>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {testimonials.map((x) => (
              <figure key={x.name} className="glass-dark rounded-sm p-8" data-testid={`testimonial-${x.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <blockquote className="text-sm text-slate-300 leading-relaxed">"{x.quote}"</blockquote>
                <figcaption className="mt-6">
                  <p className="font-heading font-bold text-white text-sm">{x.name}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{x.role}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 lg:px-10 py-24">
        <div className="max-w-5xl mx-auto glass-dark rounded-sm p-10 md:p-16">
          <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-white max-w-lg">
            {t("b2b.cta.title")}
          </h2>
          <p className="mt-4 text-slate-400 max-w-xl">
            {t("b2b.cta.text")}
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/contact" data-testid="footer-cta-contact" className="bg-[#970C10] hover:bg-[#7A0A0D] text-white font-semibold px-7 py-3.5 rounded-sm transition-colors duration-200">
              {t("b2b.cta.btn1")}
            </Link>
            <Link to="/flights" data-testid="footer-cta-flights" className="text-slate-300 hover:text-white font-semibold px-7 py-3.5 transition-colors duration-200 flex items-center gap-2">
              {t("b2b.cta.btn2")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
